'use client';

import { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [shakeError, setShakeError] = useState(false);

  const emailValid = email.includes('@') && email.includes('.');

  const triggerShake = () => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('ইমেইল ঠিকানা দিন।');
      triggerShake();
      return;
    }
    setLoading(true);
    setError('');

    try {
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
      });

      if (resetError) {
        if (resetError.message?.toLowerCase().includes('rate limit') || (resetError as any)?.status === 429) {
          setError('অনেক বেশি চেষ্টা করা হয়েছে। কিছুক্ষণ অপেক্ষা করে আবার চেষ্টা করুন।');
        } else {
          setError(resetError.message || 'পাসওয়ার্ড রিসেট ইমেইল পাঠাতে ব্যর্থ হয়েছে।');
        }
        triggerShake();
        return;
      }

      setSent(true);
      toast.success('রিসেট লিংক পাঠানো হয়েছে!', {
        description: `${email} ঠিকানায় পাসওয়ার্ড রিসেটের লিংক পাঠানো হয়েছে।`,
        duration: 5000,
      });
    } catch (err: any) {
      setError(err?.message || 'কিছু একটা সমস্যা হয়েছে। আবার চেষ্টা করুন।');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <AppLogo size={40} />
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">AgroGuard AI</p>
              <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">পাসওয়ার্ড ভুলে গেছেন?</h1>
            </div>
          </div>

          {!sent ? (
            <>
              <p className="text-sm text-gray-500 mb-6">
                আপনার অ্যাকাউন্টের ইমেইল ঠিকানা দিন। আমরা পাসওয়ার্ড রিসেটের লিংক পাঠাবো।
              </p>

              {error && (
                <div className={`bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4 flex items-start gap-2 error-slide-in ${shakeError ? 'shake' : ''}`}>
                  <AlertCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    ইমেইল ঠিকানা <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      className={`w-full pl-10 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all field-highlight ${
                        emailValid
                          ? 'border-green-400 ring-2 ring-green-100 bg-green-50/20' :'border-gray-200 focus:border-green-400 focus:ring-green-100'
                      }`}
                      required
                    />
                    {emailValid && (
                      <CheckCircle size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-green-500 check-pop" />
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full min-h-[48px] py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 btn-ripple"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {loading ? 'পাঠানো হচ্ছে...' : 'রিসেট লিংক পাঠান'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6 success-pop">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle size={32} className="text-green-600 check-pop" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">ইমেইল পাঠানো হয়েছে!</h2>
              <p className="text-sm text-gray-500 mb-6">
                <span className="font-semibold text-gray-700">{email}</span> ঠিকানায় পাসওয়ার্ড রিসেটের লিংক পাঠানো হয়েছে। আপনার ইনবক্স চেক করুন।
              </p>
              <p className="text-xs text-gray-400 mb-6">
                ইমেইল না পেলে স্প্যাম ফোল্ডার চেক করুন অথবা কিছুক্ষণ পর আবার চেষ্টা করুন।
              </p>
              <button
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-sm text-green-600 hover:underline font-medium"
              >
                অন্য ইমেইল দিয়ে চেষ্টা করুন
              </button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <Link
              href="/login"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              লগইন পেজে ফিরে যান
            </Link>
          </div>
        </div>
      </div>

      {/* Right: Decorative Panel */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-green-600 to-green-800 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-white" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-white" />
        </div>
        <div className="relative z-10 text-white text-center max-w-sm">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Mail size={36} className="text-white" />
          </div>
          <h2 className="text-3xl font-extrabold mb-4">পাসওয়ার্ড পুনরুদ্ধার</h2>
          <p className="text-green-100 text-base leading-relaxed">
            আপনার ইমেইলে একটি নিরাপদ লিংক পাঠানো হবে। লিংকে ক্লিক করে নতুন পাসওয়ার্ড সেট করুন।
          </p>
        </div>
      </div>
    </div>
  );
}
